import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const RouterContext = createContext(null);
const ParamsContext = createContext({});

function readPathname() {
  return window.location.pathname;
}

export function Router({ children }) {
  const [pathname, setPathname] = useState(readPathname);

  useEffect(() => {
    const onPopState = () => setPathname(readPathname());
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  const navigate = useCallback((to, options = {}) => {
    const path = typeof to === "string" ? to : to.pathname;
    if (options.replace) {
      window.history.replaceState(null, "", path);
    } else {
      window.history.pushState(null, "", path);
    }
    setPathname(readPathname());
  }, []);

  const value = useMemo(
    () => ({
      pathname,
      navigate,
    }),
    [pathname, navigate]
  );

  return (
    <RouterContext.Provider value={value}>{children}</RouterContext.Provider>
  );
}

export function useLocation() {
  const ctx = useContext(RouterContext);
  if (!ctx) {
    throw new Error("useLocation must be used within Router");
  }
  return { pathname: ctx.pathname };
}

export function useNavigate() {
  const ctx = useContext(RouterContext);
  if (!ctx) {
    throw new Error("useNavigate must be used within Router");
  }
  return ctx.navigate;
}

export function ParamsProvider({ params, children }) {
  return (
    <ParamsContext.Provider value={params}>{children}</ParamsContext.Provider>
  );
}

export function useParams() {
  return useContext(ParamsContext);
}

export function Navigate({ to, replace = false }) {
  const navigate = useNavigate();

  useEffect(() => {
    navigate(to, { replace });
  }, [navigate, to, replace]);

  return null;
}
